import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Reveal from "../components/Reveal";
import usePageMeta from "../lib/usePageMeta";
import { ArrowRight, BookOpen, MapPin, X } from "lucide-react";

function postMatchesState(post, stateLower) {
  if (!stateLower) return true;
  const tags = (post.tags || []).map((t) => (t || "").toLowerCase());
  if (tags.some((t) => t.includes(stateLower))) return true;
  const title = (post.title || "").toLowerCase();
  const excerpt = (post.excerpt || "").toLowerCase();
  const content = (post.content || "").toLowerCase();
  return (
    title.includes(stateLower) ||
    excerpt.includes(stateLower) ||
    content.includes(stateLower)
  );
}

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params] = useSearchParams();
  const stateFilter = (params.get("state") || "").trim();
  const stateLower = stateFilter.toLowerCase();

  usePageMeta({
    title: stateFilter
      ? `Blog · Surplus Funds in ${stateFilter} | American Asset Recovery Agency`
      : "Blog · Surplus Foreclosure Funds Insights | American Asset Recovery Agency",
    description: stateFilter
      ? `Guides, timelines, and claim-process insights about surplus foreclosure funds recovery in ${stateFilter}.`
      : "Practical, plain-English guides on surplus foreclosure funds recovery — eligibility, timelines, state rules, and how to claim what's yours.",
    canonical: stateFilter
      ? `https://americanassetrecoveryagency.com/blog?state=${encodeURIComponent(stateFilter)}`
      : "https://americanassetrecoveryagency.com/blog",
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/blog");
        setPosts(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filteredPosts = useMemo(
    () => (stateLower ? posts.filter((p) => postMatchesState(p, stateLower)) : posts),
    [posts, stateLower]
  );

  return (
    <div className="min-h-screen bg-[#F7F5F0]" data-testid="blog-index">
      <Header />
      <main className="pt-32 pb-20">
        <div className="max-w-6xl mx-auto px-6">
          <Reveal>
            <p className="text-sm sm:text-base font-bold uppercase tracking-[0.18em] text-[#FF008F] mb-4">
              {stateFilter ? `Insights · ${stateFilter}` : "Insights & Guides"}
            </p>
            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#0F172A] leading-tight">
              {stateFilter
                ? `Surplus foreclosure funds in ${stateFilter}.`
                : "Surplus foreclosure funds, explained."}
            </h1>
            <p className="mt-5 text-lg text-[#475569] max-w-2xl">
              {stateFilter
                ? `Articles and guides tagged with ${stateFilter} — plus general resources that apply nationwide.`
                : "Plain-English guides on how surplus funds work, who qualifies, what the claim process looks like, and how to avoid common pitfalls."}
            </p>
          </Reveal>

          {!stateFilter && (
            <Reveal delay={0.05}>
              <div className="mt-10 sm:mt-12 bg-white rounded-2xl border border-[#E2DDD3] p-6 sm:p-8 shadow-sm text-[#334155] leading-relaxed space-y-4 max-w-3xl" data-testid="blog-intro-copy">
                <h2 className="font-serif-display text-2xl sm:text-3xl text-[#0F172A] leading-tight">
                  What are surplus foreclosure funds — and why do so many families miss them?
                </h2>
                <p>
                  When a home is sold at a foreclosure auction for more than the outstanding mortgage debt, unpaid taxes, and fees, the leftover money — called <strong>surplus funds</strong>, <strong>excess proceeds</strong>, or <strong>overage funds</strong> — legally belongs to the previous homeowner. In many cases these amounts range from a few thousand dollars to more than a hundred thousand, depending on the property, the sale price, and the outstanding debt.
                </p>
                <p>
                  Here is the catch: <strong>county governments are not required to actively find you.</strong> The money sits in a court registry or unclaimed-funds trust, and unless a former owner or authorized representative files a written petition — with the correct documents, in the correct court, before the statutory deadline — those funds are eventually absorbed by the state. Deadlines vary widely: some states allow 1 year, others allow 3–5+ years. Missing paperwork, disputed heirs, and competing junior lienholders can all delay or block a claim.
                </p>
                <p>
                  This blog is where our recovery specialists share plain-English answers to the questions we hear every day: <em>Am I even eligible? How do I prove I owned the home? What happens if the previous owner has passed away? How long does it really take to get the check?</em> We publish practical guides, state-by-state notes on filing rules, red flags of surplus-funds scams, and step-by-step walkthroughs of a real claim from records search to funding.
                </p>
                <p>
                  If you were foreclosed on — or you inherited a property that was foreclosed — there is a real chance surplus funds are waiting in your name. Our <strong>free claim check</strong> is exactly what it sounds like: we search public county records at no cost and tell you honestly whether we believe funds exist. There are no upfront fees, ever. We only get paid on successful recovery, and the fee is disclosed in writing before any engagement begins.
                </p>
                <p>
                  Not sure where to start? Read the <Link to="/blog/surplus-foreclosure-funds-complete-guide" className="text-[#1E3A8A] hover:text-[#FF008F] font-medium">complete homeowner&apos;s guide</Link>, or jump straight to <Link to="/blog/surplus-funds-claim-process-step-by-step" className="text-[#1E3A8A] hover:text-[#FF008F] font-medium">what the claim process looks like step by step</Link>.
                </p>
              </div>
            </Reveal>
          )}

          {stateFilter && (
            <Reveal delay={0.05}>
              <div
                className="mt-8 inline-flex items-center gap-3 bg-white border border-[#E2DDD3] rounded-full px-4 py-2 shadow-sm"
                data-testid="blog-state-filter-banner"
              >
                <MapPin className="w-4 h-4 text-[#FF008F]" />
                <span className="text-sm text-[#0F172A]">
                  Filtered by <span className="font-bold">{stateFilter}</span>
                  <span className="text-[#64748B] ml-2">· {filteredPosts.length} result{filteredPosts.length === 1 ? "" : "s"}</span>
                </span>
                <Link
                  to="/blog"
                  className="ml-2 inline-flex items-center gap-1 text-xs text-[#1E3A8A] hover:text-[#FF008F] font-semibold"
                  data-testid="blog-clear-filter"
                >
                  <X className="w-3.5 h-3.5" />
                  Clear
                </Link>
              </div>
            </Reveal>
          )}

          <div className="mt-12 sm:mt-16">
            {loading ? (
              <div className="text-sm text-[#64748B]">Loading…</div>
            ) : filteredPosts.length === 0 ? (
              <div className="bg-white rounded-2xl border border-[#E2DDD3] p-12 text-center" data-testid="blog-empty-state">
                <BookOpen className="w-10 h-10 mx-auto text-[#FF008F] mb-3" />
                <p className="font-serif-display text-xl text-[#0F172A]">
                  {stateFilter
                    ? `No ${stateFilter}-specific articles yet.`
                    : "Articles coming soon."}
                </p>
                <p className="text-sm text-[#64748B] mt-1 max-w-md mx-auto">
                  {stateFilter
                    ? `We're actively researching ${stateFilter} county records and case law. In the meantime, our general guides apply nationwide.`
                    : "We're working on educational content for homeowners."}
                </p>
                {stateFilter && (
                  <Link
                    to="/blog"
                    className="inline-flex items-center gap-1 mt-5 text-sm text-[#1E3A8A] hover:text-[#FF008F] font-semibold"
                    data-testid="blog-view-all-link"
                  >
                    View all articles <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                )}
              </div>
            ) : (
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {filteredPosts.map((p, i) => (
                  <Reveal key={p.id} delay={i * 0.08}>
                    <Link
                      to={`/blog/${p.slug}`}
                      className="group h-full flex flex-col bg-white rounded-2xl border border-[#E2DDD3] shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 overflow-hidden"
                      data-testid={`blog-card-${p.slug}`}
                    >
                      {p.cover_image_url && (
                        <div className="relative aspect-[16/10] overflow-hidden bg-[#EFEAE0]">
                          <img src={p.cover_image_url} alt={p.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                        </div>
                      )}
                      <div className="p-6 flex flex-col flex-1">
                        {p.tags?.[0] && (
                          <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#FF008F] mb-3">{p.tags[0]}</p>
                        )}
                        <h2 className="font-serif-display text-xl text-[#0F172A] leading-tight">{p.title}</h2>
                        <p className="mt-3 text-sm text-[#475569] leading-relaxed flex-1">{p.excerpt}</p>
                        <div className="mt-5 flex items-center justify-between text-xs text-[#64748B]">
                          <span>{p.published_at ? new Date(p.published_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : ""}</span>
                          <span className="inline-flex items-center text-[#1E3A8A] group-hover:text-[#FF008F] font-medium">
                            Read <ArrowRight className="w-3.5 h-3.5 ml-1 group-hover:translate-x-0.5 transition-transform" />
                          </span>
                        </div>
                      </div>
                    </Link>
                  </Reveal>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
